import { useMemo, useCallback } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';

/**
 * Optimized permissions hook that provides memoized permission checks
 * and common permission patterns for better performance
 */
export function useOptimizedPermissions() {
  const { permissions, hasPermission, hasAnyPermission, hasAllPermissions, loading, error } = usePermissions();

  // Memoized common permission checks
  const permissionChecks = useMemo(() => ({
    // System permissions
    isSuperadmin: hasPermission('system.override'),
    isAdmin: hasPermission('settings.admin'),
    canManageSettings: hasPermission('manage_settings'),
    canManageTenant: hasPermission('tenant.manage'),

    // User management permissions
    canManageAllUsers: hasPermission('user.manage_all'),
    canManageTeamUsers: hasPermission('user.manage_team'),
    canViewAllUsers: hasPermission('user.view_all'),
    canEditUsers: hasPermission('edit_users'),
    canDeleteUsers: hasPermission('delete_users'),

    // Project permissions
    canViewAllProjects: hasPermission('project.view_all'),
    canViewTeamProjects: hasPermission('project.view_team'),
    canManageProjects: hasPermission('manage_projects'),
    canEditProjects: hasPermission('edit_projects'),
    canDeleteProjects: hasPermission('delete_projects'),

    // Pipeline permissions
    canViewCommercialPipeline: hasPermission('view_commercial_pipeline'),
    canViewResidentialPipeline: hasPermission('view_residential_pipeline'),
    canViewOnboardingPipeline: hasPermission('view_onboarding_pipeline'),

    // Contract permissions
    canManageContracts: hasPermission('manage_contracts'),
    canViewContracts: hasPermission('view_contracts'),

    // Work order permissions
    canManageWorkOrders: hasPermission('manage_work_orders'),
    canEditWorkOrders: hasPermission('edit_work_orders'),
    canViewWorkOrders: hasPermission('view_work_orders'),

    // Sales team permissions
    canManageSalesTeam: hasPermission('manage_sales_team'),
    canViewSalesTeam: hasPermission('view_sales_team'),

    // Reporting permissions
    canAccessReporting: hasPermission('reporting.admin'),

    // Role management
    canManageRoles: hasPermission('manage_roles'),
  }), [hasPermission]);

  // Memoized role-based access patterns
  const roleAccess = useMemo(() => ({
    // High-level role checks
    isSystemAdmin: permissionChecks.isSuperadmin || permissionChecks.isAdmin,
    isBizDevRep: permissionChecks.canViewTeamProjects && !permissionChecks.canViewAllProjects,
    isSalesRep: hasPermission('project.view_team') && !permissionChecks.canManageProjects,
    isInternalPM: permissionChecks.canManageProjects && !(permissionChecks.isSuperadmin || permissionChecks.isAdmin),
    isInstallerPM: hasPermission('view_work_orders') && !permissionChecks.canManageWorkOrders,

    // Feature access patterns
    canAccessAdminPanel: (permissionChecks.isSuperadmin || permissionChecks.isAdmin) || permissionChecks.canManageSettings,
    canAccessUserManagement: permissionChecks.canManageAllUsers || permissionChecks.canManageTeamUsers,
    canAccessProjectManagement: permissionChecks.canManageProjects || permissionChecks.canViewAllProjects,
    canAccessReporting: permissionChecks.canAccessReporting || (permissionChecks.isSuperadmin || permissionChecks.isAdmin),
    canAccessSettings: permissionChecks.canManageSettings || (permissionChecks.isSuperadmin || permissionChecks.isAdmin),
  }), [permissionChecks, hasPermission]);

  // Optimized permission checking functions
  const checkPermission = useCallback((permission: string): boolean => {
    return permissions.has(permission);
  }, [permissions]);

  const checkAnyPermission = useCallback((permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.has(permission));
  }, [permissions]);

  const checkAllPermissions = useCallback((permissionList: string[]): boolean => {
    return permissionList.every(permission => permissions.has(permission));
  }, [permissions]);

  // Common permission patterns
  const commonPatterns = useMemo(() => ({
    // Project access patterns
    canViewProject: (projectId: string) => {
      // Superadmin can view all projects
      if (permissionChecks.isSuperadmin) return true;
      
      // Admin can view all projects
      if (permissionChecks.isAdmin) return true;
      
      // Users with project.view_all can view all projects
      if (permissionChecks.canViewAllProjects) return true;
      
      // Users with project.view_team can view team projects
      if (permissionChecks.canViewTeamProjects) return true;
      
      // TODO: Add project-specific permission checks here
      return false;
    },

    // User access patterns
    canViewUser: (userId: string) => {
      // Superadmin can view all users
      if (permissionChecks.isSuperadmin) return true;
      
      // Admin can view all users
      if (permissionChecks.isAdmin) return true;
      
      // Users with user.view_all can view all users
      if (permissionChecks.canViewAllUsers) return true;
      
      // Users can view themselves
      // TODO: Add user ID comparison here
      return false;
    },

    // Pipeline access patterns
    canViewPipeline: (pipelineType: 'commercial' | 'residential' | 'onboarding') => {
      switch (pipelineType) {
        case 'commercial':
          return permissionChecks.canViewCommercialPipeline;
        case 'residential':
          return permissionChecks.canViewResidentialPipeline;
        case 'onboarding':
          return permissionChecks.canViewOnboardingPipeline;
        default:
          return false;
      }
    },
  }), [permissionChecks]);

  return {
    // Basic permission functions
    permissions,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    loading,
    error,

    // Pre-computed permission checks
    ...permissionChecks,

    // Role-based access patterns
    ...roleAccess,

    // Common permission patterns
    ...commonPatterns,

    // Utility functions
    isAuthorized: (requiredPermissions: string[], requireAll: boolean = false) => {
      return requireAll 
        ? checkAllPermissions(requiredPermissions)
        : checkAnyPermission(requiredPermissions);
    },

    hasRoleAccess: (rolePattern: keyof typeof roleAccess) => {
      return roleAccess[rolePattern];
    },

    getAccessibleFeatures: () => {
      const features = [];
      
      if (roleAccess.canAccessAdminPanel) features.push('admin_panel');
      if (roleAccess.canAccessUserManagement) features.push('user_management');
      if (roleAccess.canAccessProjectManagement) features.push('project_management');
      if (roleAccess.canAccessReporting) features.push('reporting');
      if (roleAccess.canAccessSettings) features.push('settings');
      
      return features;
    },
  };
}

// Convenience hooks for specific permission patterns
export function useAdminAccess() {
  const { isSystemAdmin, canAccessAdminPanel, loading } = useOptimizedPermissions();
  return { hasAccess: isSystemAdmin || canAccessAdminPanel, loading };
}

export function useProjectAccess() {
  const { canViewAllProjects, canViewTeamProjects, canManageProjects, loading } = useOptimizedPermissions();
  return { 
    canViewAll: canViewAllProjects, 
    canViewTeam: canViewTeamProjects, 
    canManage: canManageProjects, 
    loading 
  };
}

export function useUserManagementAccess() {
  const { canManageAllUsers, canManageTeamUsers, canViewAllUsers, loading } = useOptimizedPermissions();
  return { 
    canManageAll: canManageAllUsers, 
    canManageTeam: canManageTeamUsers, 
    canViewAll: canViewAllUsers, 
    loading 
  };
}

export function usePipelineAccess() {
  const { 
    canViewCommercialPipeline, 
    canViewResidentialPipeline, 
    canViewOnboardingPipeline, 
    loading 
  } = useOptimizedPermissions();
  
  return { 
    canViewCommercial: canViewCommercialPipeline,
    canViewResidential: canViewResidentialPipeline,
    canViewOnboarding: canViewOnboardingPipeline,
    loading 
  };
}
